import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  inject,
  TemplateRef,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ICliente, ILuminaria, IUbicacionBase } from 'modelos/src';
import * as turf from '@turf/turf';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  CodigoEstadoLuminaria,
  determinarCodigoEstado,
  presentacionEstado,
} from '../../estado/estado-luminaria';
import {
  FiltrosLuminariasModalComponent,
  ILuminariasFiltros,
} from '../filtros-luminarias-modal/filtros-luminarias-modal.component';

/** Qué hacer con las luminarias que queden dentro del polígono dibujado. */
export type AccionPoligonoLuminarias = 'cambiar-grupo' | 'exportar-excel';

/**
 * Acciones de polígono, data-driven: agregar una es sumar una entrada acá (la
 * grilla se acomoda sola) + el case correspondiente en el mapa.
 */
export const ACCIONES_POLIGONO: {
  key: AccionPoligonoLuminarias;
  label: string;
  icono: string;
  /**
   * Descripción larga. Va SOLO como `aria-label`: como tooltip tapaba los
   * demás ítems del menú y no dejaba elegir.
   */
  tooltip: string;
}[] = [
  {
    key: 'cambiar-grupo',
    label: 'Cambiar grupo',
    // Mismo ícono que usa el listado para "Grupo de luminarias".
    icono: 'workspaces',
    tooltip:
      'Dibujá un polígono para cambiar el grupo de las luminarias dentro de él',
  },
  {
    key: 'exportar-excel',
    label: 'Descargar Excel',
    icono: 'file_download',
    tooltip:
      'Dibujá un polígono para exportar a Excel las luminarias dentro de él',
  },
];

const FILTROS_VACIOS: ILuminariasFiltros = {
  direccion: '',
  identificacion: '',
  dispositivo: '',
  grupos: [],
  zonas: [],
  clientes: [],
};

@Component({
  selector: 'app-flotante-luminarias',
  templateUrl: './flotante-luminarias.component.html',
  styleUrl: './flotante-luminarias.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlotanteLuminariasComponent
  implements OnInit, OnDestroy, OnChanges
{
  helper = inject(HelperService);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  /** Las que tienen ubicación real: el total del contador. */
  @Input() luminariasLength?: number;
  @Input() luminarias: ILuminaria[] = [];
  /** Las que no tienen ubicación real: no se dibujan ni se cuentan. */
  @Input() sinUbicacion = 0;
  @Input() zonas: IUbicacionBase<'Luminarias'>[] = [];
  /** Dona del resumen; el panel la pone a la izquierda de los Estados. */
  @Input() dona: TemplateRef<unknown> | null = null;

  @Output() luminariaChange = new EventEmitter<ILuminaria[]>();
  @Output() clusterChange = new EventEmitter<boolean>();
  @Output() poligonoClientesChange = new EventEmitter<boolean>();
  @Output() poligonoZonasChange = new EventEmitter<boolean>();
  @Output() textoClientesChange = new EventEmitter<boolean>();
  @Output() textoZonasChange = new EventEmitter<boolean>();
  @Output() gatewayChange = new EventEmitter<boolean>();
  @Output() clientesSeleccionadosChange = new EventEmitter<ICliente[]>();
  @Output() accionPoligono = new EventEmitter<AccionPoligonoLuminarias>();

  public readonly accionesPoligono = ACCIONES_POLIGONO;

  public clusterEnabled = false;
  public poligonoClientesEnabled = false;
  public poligonoZonasEnabled = true;
  public textoClientesEnabled = true;
  public textoZonasEnabled = true;
  public gatewayEnabled = true;
  public expansionPanelExpanded = true;
  public estados: { nombre?: string; cant?: number; icono?: string }[] = [];

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    const wasMobile = !this.expansionPanelExpanded;
    const isMobileNow = event.target.innerWidth <= 768;
    if (wasMobile !== isMobileNow) {
      this.expansionPanelExpanded = !isMobileNow;
    }
  }

  // Buscador único multi-campo + filtros del modal
  public textoBusqueda = '';
  public filtros: ILuminariasFiltros = { ...FILTROS_VACIOS };

  public filtrosEstado: string[] = [];

  public luminariasFiltradas: ILuminaria[] = [];

  estadosLuminarias = ([0, 1, 2, 3, 4, 5] as CodigoEstadoLuminaria[]).map(
    (codigo) => ({
      nombre: presentacionEstado(codigo).label,
      icono: presentacionEstado(codigo).iconoSvg,
    }),
  );

  public clientesSeleccionados: ICliente[] = [];

  public setEstadosDisponibles() {
    this.estados = this.estadosLuminarias.map((estado) => ({
      ...estado,
      cant: this.contarLuminariasPorEstadoTotal(estado.nombre),
    }));
  }

  public get hayFiltrosActivos(): boolean {
    return (
      this.textoBusqueda.trim().length > 0 ||
      this.filtros.direccion.length > 0 ||
      this.filtros.identificacion.length > 0 ||
      this.filtros.dispositivo.length > 0 ||
      this.filtros.grupos.length > 0 ||
      this.filtros.zonas.length > 0 ||
      this.filtros.clientes.length > 0 ||
      this.filtrosEstado.length !== this.estados.length
    );
  }

  public get hayFiltrosModalActivos(): boolean {
    return (
      this.filtros.direccion.length > 0 ||
      this.filtros.identificacion.length > 0 ||
      this.filtros.dispositivo.length > 0 ||
      this.filtros.grupos.length > 0 ||
      this.filtros.zonas.length > 0 ||
      this.filtros.clientes.length > 0
    );
  }

  public get hayBusquedaOFiltrosModal(): boolean {
    return this.textoBusqueda.trim().length > 0 || this.hayFiltrosModalActivos;
  }

  public get hayEstadosFiltrados(): boolean {
    return (
      this.estados.length > 0 &&
      this.filtrosEstado.length !== this.estados.length
    );
  }

  private normalizar(texto: string): string {
    return texto
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  // Busca el término en varios campos de una luminaria
  private coincideBusquedaGlobal(l: ILuminaria, termino: string): boolean {
    if (!termino) return true;
    const t = this.normalizar(termino);
    const campos: string[] = [
      l.direccion ?? '',
      l.identificacion ?? '',
      l.dispositivo?.deveui ?? '',
      l.dispositivo?.name ?? '',
      ...((l.grupos ?? []).map((g) => g.nombre ?? '') as string[]),
    ];
    return campos.some((c) => this.normalizar(c).includes(t));
  }

  // Filtra por zonas (ubicaciones categoría Luminarias): una luminaria entra si
  // su punto cae dentro de alguno de los polígonos seleccionados. Los polígonos
  // se arman una sola vez por corrida, no por luminaria.
  private filtrarPorZonas(lista: ILuminaria[]): ILuminaria[] {
    const ids = this.filtros.zonas;
    if (!ids.length) return lista;

    const poligonos = (this.zonas ?? [])
      .filter(
        (z) =>
          z._id &&
          ids.includes(z._id) &&
          (z.geojson?.type === 'Polygon' || z.geojson?.type === 'MultiPolygon'),
      )
      .map((z) => z.geojson as GeoJSON.Polygon | GeoJSON.MultiPolygon);

    if (!poligonos.length) return lista;

    return lista.filter((l) => {
      const coords = l.ubicacion?.coordinates;
      if (!coords || coords.length < 2) return false;
      const punto = turf.point([coords[0], coords[1]]);
      return poligonos.some((poly) => turf.booleanPointInPolygon(punto, poly));
    });
  }

  private aplicarFiltroEstado(lista: ILuminaria[]): ILuminaria[] {
    if (this.filtrosEstado.length === 0) return [];
    return lista.filter((lum) => {
      const label = presentacionEstado(determinarCodigoEstado(lum)).label;
      return this.filtrosEstado.includes(label);
    });
  }

  public filtrarLuminarias(): void {
    this.setEstadosDisponibles();

    this.clientesSeleccionadosChange.emit(this.clientesSeleccionados);

    let resultado = [...this.luminarias];

    // Buscador global multi-campo
    const termino = this.textoBusqueda.trim();
    if (termino.length > 0) {
      resultado = resultado.filter((l) =>
        this.coincideBusquedaGlobal(l, termino),
      );
    }

    // Filtros del modal
    if (this.filtros.direccion.length >= 1) {
      const t = this.normalizar(this.filtros.direccion);
      resultado = resultado.filter((l) =>
        this.normalizar(l.direccion ?? '').includes(t),
      );
    }

    if (this.filtros.identificacion.length >= 1) {
      const t = this.normalizar(this.filtros.identificacion);
      resultado = resultado.filter((l) =>
        this.normalizar(l.identificacion ?? '').includes(t),
      );
    }

    if (this.filtros.dispositivo.length >= 1) {
      const t = this.normalizar(this.filtros.dispositivo);
      resultado = resultado.filter((l) => {
        const name = this.normalizar(l.dispositivo?.name ?? '');
        const deveui = this.normalizar(l.dispositivo?.deveui ?? '');
        return name.includes(t) || deveui.includes(t);
      });
    }

    if (this.filtros.grupos.length > 0) {
      resultado = resultado.filter(
        (l) =>
          Array.isArray(l.grupos) &&
          l.grupos.some((g) => this.filtros.grupos.includes(g._id)),
      );
    }

    if (this.filtros.clientes.length > 0) {
      resultado = resultado.filter((l) =>
        this.filtros.clientes.includes(l.idCliente),
      );
    }

    // Filtro por zonas (punto en polígono)
    resultado = this.filtrarPorZonas(resultado);

    // Filtro por estado (chips)
    resultado = this.aplicarFiltroEstado(resultado);

    this.luminariasFiltradas = resultado;
    this.luminariaChange.emit(this.luminariasFiltradas);
  }

  public contarLuminariasPorEstadoTotal(estado: string): number {
    let fuente = [...this.luminarias];

    const termino = this.textoBusqueda.trim();
    if (termino.length > 0) {
      fuente = fuente.filter((l) => this.coincideBusquedaGlobal(l, termino));
    }

    if (this.filtros.direccion.length >= 1) {
      const t = this.normalizar(this.filtros.direccion);
      fuente = fuente.filter((l) =>
        this.normalizar(l.direccion ?? '').includes(t),
      );
    }
    if (this.filtros.identificacion.length >= 1) {
      const t = this.normalizar(this.filtros.identificacion);
      fuente = fuente.filter((l) =>
        this.normalizar(l.identificacion ?? '').includes(t),
      );
    }
    if (this.filtros.dispositivo.length >= 1) {
      const t = this.normalizar(this.filtros.dispositivo);
      fuente = fuente.filter((l) => {
        const name = this.normalizar(l.dispositivo?.name ?? '');
        const deveui = this.normalizar(l.dispositivo?.deveui ?? '');
        return name.includes(t) || deveui.includes(t);
      });
    }
    if (this.filtros.grupos.length > 0) {
      fuente = fuente.filter(
        (l) =>
          Array.isArray(l.grupos) &&
          l.grupos.some((g) => this.filtros.grupos.includes(g._id)),
      );
    }
    if (this.filtros.clientes.length > 0) {
      fuente = fuente.filter((l) =>
        this.filtros.clientes.includes(l.idCliente),
      );
    }

    fuente = this.filtrarPorZonas(fuente);

    return fuente.filter(
      (lum) => presentacionEstado(determinarCodigoEstado(lum)).label === estado,
    ).length;
  }

  public onToggleCluster() {
    this.clusterEnabled = !this.clusterEnabled;
    this.clusterChange.emit(this.clusterEnabled);
    localStorage.setItem(
      'mostrarClusterLuminaria',
      String(this.clusterEnabled),
    );
    this.cdr.markForCheck();
  }

  public onTogglePoligonoClientes() {
    this.poligonoClientesEnabled = !this.poligonoClientesEnabled;
    this.poligonoClientesChange.emit(this.poligonoClientesEnabled);
    localStorage.setItem(
      'mostrarPoligonoClientesLuminaria',
      String(this.poligonoClientesEnabled),
    );
    this.cdr.markForCheck();
  }

  public onToggleTextoClientes() {
    this.textoClientesEnabled = !this.textoClientesEnabled;
    this.textoClientesChange.emit(this.textoClientesEnabled);
    localStorage.setItem(
      'mostrarTextoPoligonoClientesLuminaria',
      String(this.textoClientesEnabled),
    );
    this.cdr.markForCheck();
  }

  public onTogglePoligonoZonas() {
    this.poligonoZonasEnabled = !this.poligonoZonasEnabled;
    this.poligonoZonasChange.emit(this.poligonoZonasEnabled);
    localStorage.setItem(
      'mostrarPoligonoZonasLuminaria',
      String(this.poligonoZonasEnabled),
    );
    this.cdr.markForCheck();
  }

  public onToggleTextoZonas() {
    this.textoZonasEnabled = !this.textoZonasEnabled;
    this.textoZonasChange.emit(this.textoZonasEnabled);
    localStorage.setItem(
      'mostrarTextoPoligonoZonasLuminaria',
      String(this.textoZonasEnabled),
    );
    this.cdr.markForCheck();
  }

  public onToggleGateway() {
    this.gatewayEnabled = !this.gatewayEnabled;
    this.gatewayChange.emit(this.gatewayEnabled);
    localStorage.setItem(
      'mostrarGatewaysLuminaria',
      String(this.gatewayEnabled),
    );
    this.cdr.markForCheck();
  }

  public onBusquedaChange(): void {
    this.filtrarLuminarias();
  }

  public limpiarBusqueda(): void {
    this.textoBusqueda = '';
    this.filtrarLuminarias();
  }

  public toggleEstado(estado: string): void {
    const idx = this.filtrosEstado.indexOf(estado);
    if (idx > -1) {
      this.filtrosEstado.splice(idx, 1);
    } else {
      this.filtrosEstado.push(estado);
    }
    this.filtrarLuminarias();
  }

  private filtrosDialogRef?: MatDialogRef<FiltrosLuminariasModalComponent>;

  public abrirDialogFiltros(): void {
    // Una sola instancia: si ya está abierto, no apilo otro modal (se perdía la
    // info y quedaban varios superpuestos).
    if (this.filtrosDialogRef) return;

    const ref = this.dialog.open(FiltrosLuminariasModalComponent, {
      width: '400px',
      maxWidth: '92vw',
      maxHeight: '88vh',
      panelClass: 'filtros-luminarias-dialog',
      // No-modal: sin backdrop el mapa detrás queda usable (mover, zoom, click).
      hasBackdrop: false,
      // Solo se cierra con la X (cancelar()): ni click afuera ni Escape.
      disableClose: true,
      data: {
        filtros: { ...this.filtros },
        zonas: this.zonas,
        // Filtrado en vivo: cada cambio en el modal refiltra el mapa al toque,
        // sin necesidad de cerrar ni presionar "Aplicar".
        onLiveChange: (filtros: ILuminariasFiltros) => {
          this.filtros = filtros;
          this.filtrarLuminarias();
          this.cdr.markForCheck();
        },
      },
    });
    this.filtrosDialogRef = ref;
    ref.afterClosed().subscribe((res: ILuminariasFiltros | undefined) => {
      this.filtrosDialogRef = undefined;
      if (res) {
        this.filtros = res;
        this.filtrarLuminarias();
        this.cdr.markForCheck();
      }
    });
  }

  // Limpia solo buscador + filtros del modal (no toca estados)
  public limpiarSoloFiltros(): void {
    this.textoBusqueda = '';
    this.filtros = { ...FILTROS_VACIOS };
    this.filtrarLuminarias();
  }

  // Limpia solo el filtro de estados (vuelve a marcarlos todos)
  public limpiarSoloEstados(): void {
    this.filtrosEstado = this.estados.map((e) => e.nombre);
    this.filtrarLuminarias();
  }

  // Limpia todo: buscador, filtros del modal y estados
  public limpiarFiltros(): void {
    this.textoBusqueda = '';
    this.filtros = { ...FILTROS_VACIOS };
    this.filtrosEstado = this.estados.map((e) => e.nombre);
    this.filtrarLuminarias();
  }

  private getBooleanFromStorage(key: string, defaultValue: boolean): boolean {
    const value = localStorage.getItem(key);
    return value !== null ? value === 'true' : defaultValue;
  }

  async ngOnInit(): Promise<void> {
    this.expansionPanelExpanded = window.innerWidth > 768;

    this.clusterEnabled = this.getBooleanFromStorage(
      'mostrarClusterLuminaria',
      false,
    );
    this.poligonoClientesEnabled = this.getBooleanFromStorage(
      'mostrarPoligonoClientesLuminaria',
      false,
    );
    this.poligonoZonasEnabled = this.getBooleanFromStorage(
      'mostrarPoligonoZonasLuminaria',
      true,
    );
    this.textoClientesEnabled = this.getBooleanFromStorage(
      'mostrarTextoPoligonoClientesLuminaria',
      true,
    );
    this.textoZonasEnabled = this.getBooleanFromStorage(
      'mostrarTextoPoligonoZonasLuminaria',
      true,
    );
    this.gatewayEnabled = this.getBooleanFromStorage(
      'mostrarGatewaysLuminaria',
      true,
    );
    this.filtrosEstado = this.estadosLuminarias.map((e) => e.nombre);

    this.clusterChange.emit(this.clusterEnabled);
    this.poligonoClientesChange.emit(this.poligonoClientesEnabled);
    this.poligonoZonasChange.emit(this.poligonoZonasEnabled);
    this.textoClientesChange.emit(this.textoClientesEnabled);
    this.textoZonasChange.emit(this.textoZonasEnabled);
    this.gatewayChange.emit(this.gatewayEnabled);

    this.filtrarLuminarias();
  }

  async ngOnChanges(): Promise<void> {
    this.filtrarLuminarias();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    // Cierra el modal si sigue abierto al salir del mapa: el MatDialog no se
    // ata al ciclo de la vista y quedaba visible en otras rutas.
    this.filtrosDialogRef?.close();
    this.filtrosDialogRef = undefined;
  }
}
